import { motion } from "framer-motion";
import { Activity, Lock, ShieldCheck } from "lucide-react";
import { IMAGES } from "@/data/content";

// Decorative floating UI card — generic visual element only, not a product claim.
function FloatCard({ icon: Icon, label, className = "", duration, delay, testId }) {
  return (
    <div
      aria-hidden="true"
      data-testid={testId}
      className={`absolute ${className}`}
      style={{ animation: `hero-float ${duration}s ease-in-out ${delay}s infinite` }}
    >
      <div className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.06] px-3.5 py-2.5 shadow-[0_16px_40px_rgba(0,0,0,0.35)] backdrop-blur-md">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-iris/40 to-aqua/30 text-aqua">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/65">{label}</span>
      </div>
    </div>
  );
}

// Right-side hero focal point: holographic glass security shield inside a
// network globe, floating on a glowing tech platform. Layered with animated
// orbiting data points and decorative floating UI cards.
export default function HeroShield({ reduced = false, cx, cy }) {
  return (
    <div
      className="relative mx-auto aspect-square w-full max-w-[320px] sm:max-w-[420px] lg:max-w-[540px] xl:max-w-[580px]"
      data-testid="hero-shield-visual"
    >
      {/* atmospheric core glow behind the visual */}
      <div
        className="blob-pulse absolute left-1/2 top-1/2 h-[75%] w-[75%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-iris/20 blur-[100px]"
        aria-hidden="true"
      />
      <div
        className="blob-pulse absolute left-1/2 top-1/2 h-[50%] w-[50%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-azure/15 blur-[80px]"
        style={{ animationDelay: "-3s" }}
        aria-hidden="true"
      />

      {/* holographic security core (3D shield + padlock + network globe) */}
      <div className="absolute left-1/2 top-1/2 z-10 h-full w-full -translate-x-1/2 -translate-y-1/2">
        <motion.div
          animate={reduced ? {} : { y: [0, -12, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="h-full w-full"
          data-testid="hero-shield-core"
        >
          <img
            src={IMAGES.shieldCore}
            alt="Holographic digital security shield with padlock inside a network globe"
            className="h-full w-full select-none object-cover"
            style={{
              WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 55%, transparent 78%)",
              maskImage: "radial-gradient(ellipse 70% 70% at 50% 50%, black 55%, transparent 78%)",
            }}
            draggable={false}
          />
        </motion.div>
      </div>

      {/* orbiting data points */}
      <div className="orbit-16 absolute inset-[2%] rounded-full" aria-hidden="true">
        <span className="absolute left-1/2 top-0 -ml-1 h-2 w-2 rounded-full bg-aqua shadow-[0_0_14px_rgba(53,214,210,0.9)]" />
      </div>
      <div className="orbit-24 absolute inset-[16%] rounded-full" aria-hidden="true">
        <span className="absolute bottom-0 left-1/2 -ml-[3px] h-1.5 w-1.5 rounded-full bg-iris-bright shadow-[0_0_12px_rgba(124,77,255,0.9)]" />
      </div>

      {/* floating data cards (decorative) */}
      <motion.div style={{ x: cx, y: cy }} className="absolute inset-0 z-20 hidden sm:block" aria-hidden="true">
        <FloatCard icon={ShieldCheck} label="Protected" testId="hero-float-card-protected" className="right-[-2%] top-[6%]" duration={7} delay={0} />
        <FloatCard icon={Lock} label="Encrypted" testId="hero-float-card-encrypted" className="left-[-6%] top-[46%]" duration={9} delay={-3} />
        <FloatCard icon={Activity} label="Monitoring" testId="hero-float-card-monitoring" className="bottom-[8%] right-[2%]" duration={8} delay={-5} />
      </motion.div>
    </div>
  );
}
